import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import POSReceiptModal from '../../components/pos/POSReceiptModal';
import CustomSelect from '../../components/ui/CustomSelect';
import { useLocation } from 'react-router-dom';

const POSSaleHistory: React.FC = () => {
  const [sales, setSales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState<any>(null);
  const [filters, setFilters] = useState({ status: '', payment_method: '', date_from: '', date_to: '' });
  const [summary, setSummary] = useState<any>(null);
  const location = useLocation();
  const isAdminPanel = location.pathname.startsWith('/admin');

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { per_page: 50 };
      if (filters.status) params.status = filters.status;
      if (filters.payment_method) params.payment_method = filters.payment_method;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const response = await apiService.get('/pos/sales', { params });
      setSales(response.data || []);
    } catch (error) {
      console.error('Failed to load sales:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await apiService.get('/pos/summary/today');
      setSummary(response);
    } catch {}
  }, []);

  useEffect(() => {
    fetchSales();
    fetchSummary();
  }, [fetchSales, fetchSummary]);

  const handleViewReceipt = async (saleId: number) => {
    try {
      const response = await apiService.get(`/pos/sales/${saleId}`);
      setSelectedSale(response);
    } catch {}
  };

  // Admin panel style (white theme)
  if (isAdminPanel) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">POS Sales History</h1>
            <p className="text-gray-600">View and manage all POS transactions</p>
          </div>
        </div>

        {/* Today's Summary */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-sm font-medium text-gray-600">Today's Sales</p>
              <p className="text-2xl font-bold text-green-600">{Number(summary.total_sales).toLocaleString()} MMK</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-sm font-medium text-gray-600">Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total_transactions}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-sm font-medium text-gray-600">Cash</p>
              <p className="text-2xl font-bold text-gray-900">{Number(summary.cash_total).toLocaleString()}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-sm font-medium text-gray-600">Card/Bank/QR</p>
              <p className="text-2xl font-bold text-gray-900">
                {(Number(summary.card_total) + Number(summary.bank_transfer_total) + Number(summary.qr_total)).toLocaleString()}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-sm font-medium text-gray-600">Discounts</p>
              <p className="text-2xl font-bold text-red-600">{Number(summary.discount_total).toLocaleString()}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow flex gap-3 flex-wrap">
          <CustomSelect
            value={filters.status}
            onChange={(val) => setFilters(f => ({ ...f, status: val }))}
            placeholder="All Status"
            options={[
              { value: '', label: 'All Status' },
              { value: 'completed', label: 'Completed' },
              { value: 'voided', label: 'Voided' },
              { value: 'refunded', label: 'Refunded' },
            ]}
            className="w-40"
          />
          <CustomSelect
            value={filters.payment_method}
            onChange={(val) => setFilters(f => ({ ...f, payment_method: val }))}
            placeholder="All Payments"
            options={[
              { value: '', label: 'All Payments' },
              { value: 'cash', label: 'Cash' },
              { value: 'card', label: 'Card' },
              { value: 'bank_transfer', label: 'Bank Transfer' },
              { value: 'qr_payment', label: 'QR Payment' },
            ]}
            className="w-44"
          />
          <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sale #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cashier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total (MMK)</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={8} className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : sales.length === 0 ? (
                <tr><td colSpan={8} className="px-6 py-8 text-center text-gray-500">No sales found</td></tr>
              ) : (
                sales.map((sale) => (
                  <tr key={sale.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-mono text-gray-900">{sale.sale_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(sale.created_at).toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{sale.cashier?.name || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{sale.customer_name || 'Walk-in'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 capitalize">{sale.payment_method?.replace('_', ' ')}</td>
                    <td className="px-6 py-4 text-sm text-right font-medium text-gray-900">{Number(sale.total_amount).toLocaleString()}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        sale.status === 'completed' ? 'bg-green-100 text-green-800' :
                        sale.status === 'voided' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>{sale.status}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button onClick={() => handleViewReceipt(sale.id)} className="text-indigo-600 hover:text-indigo-900 text-sm">Receipt</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedSale && <POSReceiptModal sale={selectedSale} onClose={() => setSelectedSale(null)} />}
      </div>
    );
  }

  // POS dark theme (for /pos/sales route)
  return (
    <div className="h-full flex flex-col p-4 overflow-hidden bg-gray-900">
      {/* Today's Summary */}
      {summary && (
        <div className="grid grid-cols-5 gap-3 mb-4">
          <div className="bg-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-400">Today's Sales</p>
            <p className="text-lg font-bold text-green-400">{Number(summary.total_sales).toLocaleString()} MMK</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-400">Transactions</p>
            <p className="text-lg font-bold text-white">{summary.total_transactions}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-400">Cash</p>
            <p className="text-lg font-bold text-white">{Number(summary.cash_total).toLocaleString()}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-400">Card/Bank/QR</p>
            <p className="text-lg font-bold text-white">
              {(Number(summary.card_total) + Number(summary.bank_transfer_total) + Number(summary.qr_total)).toLocaleString()}
            </p>
          </div>
          <div className="bg-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-400">Discounts</p>
            <p className="text-lg font-bold text-red-400">{Number(summary.discount_total).toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-3">
        <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))} className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-sm text-white">
          <option value="">All Status</option>
          <option value="completed">Completed</option>
          <option value="voided">Voided</option>
          <option value="refunded">Refunded</option>
        </select>
        <select value={filters.payment_method} onChange={(e) => setFilters(f => ({ ...f, payment_method: e.target.value }))} className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-sm text-white">
          <option value="">All Payments</option>
          <option value="cash">Cash</option>
          <option value="card">Card</option>
          <option value="bank_transfer">Bank Transfer</option>
          <option value="qr_payment">QR Payment</option>
        </select>
        <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-sm text-white" />
        <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-sm text-white" />
      </div>

      {/* Sales Table */}
      <div className="flex-1 overflow-y-auto rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-800 sticky top-0">
            <tr>
              <th className="text-left p-3 text-gray-400">Sale #</th>
              <th className="text-left p-3 text-gray-400">Date</th>
              <th className="text-left p-3 text-gray-400">Cashier</th>
              <th className="text-left p-3 text-gray-400">Customer</th>
              <th className="text-left p-3 text-gray-400">Payment</th>
              <th className="text-right p-3 text-gray-400">Total</th>
              <th className="text-center p-3 text-gray-400">Status</th>
              <th className="text-center p-3 text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-400">Loading...</td></tr>
            ) : sales.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-400">No sales found</td></tr>
            ) : (
              sales.map((sale) => (
                <tr key={sale.id} className="border-t border-gray-700 hover:bg-gray-800">
                  <td className="p-3 text-white font-mono">{sale.sale_number}</td>
                  <td className="p-3 text-gray-300">{new Date(sale.created_at).toLocaleString()}</td>
                  <td className="p-3 text-gray-300">{sale.cashier?.name || '-'}</td>
                  <td className="p-3 text-gray-300">{sale.customer_name || 'Walk-in'}</td>
                  <td className="p-3 text-gray-300 capitalize">{sale.payment_method?.replace('_', ' ')}</td>
                  <td className="p-3 text-white text-right font-medium">{Number(sale.total_amount).toLocaleString()} MMK</td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      sale.status === 'completed' ? 'bg-green-900 text-green-300' :
                      sale.status === 'voided' ? 'bg-red-900 text-red-300' :
                      'bg-yellow-900 text-yellow-300'
                    }`}>{sale.status}</span>
                  </td>
                  <td className="p-3 text-center">
                    <button onClick={() => handleViewReceipt(sale.id)} className="text-blue-400 hover:text-blue-300 text-xs">Receipt</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedSale && <POSReceiptModal sale={selectedSale} onClose={() => setSelectedSale(null)} />}
    </div>
  );
};

export default POSSaleHistory;
